import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface SaengMyongEumProps {
  selectedBelt: string;
  getBeltColor: (belt: string) => string;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

const START_EAGLE_LINES = [
  "Right leg steps back into Eagle Stance (Short back stance) Fingertips forward",
  "Raise the arms like wings, keep them under the shoulders",
  "Raise the breastbone and raise the arms slightly higher, shaking the wings from the shoulders so the fingertips move; the motion comes from the arms, not the fingers. The arms are completely relaxed.",
];

function SaengMyongEumPatternTab() {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key</h4>
        <ul className="list-inside list-disc space-y-1 text-gray-700">
          <li>OH = Open Hand</li>
          <li>R = Right</li>
          <li>L = Left</li>
          <li>S = Stay in Stance</li>
          <li>UB = Upper Block</li>
          <li>LB = Low Block</li>
          <li>M = Middle</li>
          <li>Sudo = Knife-hand strike</li>
        </ul>
      </div>

      <div className="space-y-2 text-gray-700">
        <p className="font-medium">START:</p>
        <p className="font-semibold text-taekwondo-red">Joonbi</p>
        <p>Bow, Junbee (5 count)</p>
        <p>Both say &quot;Saeng Myeong&quot; = A says &quot;Um.&quot; B says &quot;Yang&quot; Both say &quot;Life!&quot;</p>
        <ol className="mt-3 list-none space-y-2">
          {START_EAGLE_LINES.map((line, index) => (
            <li key={index} className="flex gap-2">
              <span className="shrink-0 font-semibold text-taekwondo-red tabular-nums">{index + 1}.</span>
              <span>{line}</span>
            </li>
          ))}
        </ol>
      </div>

      <SaengMyongEumMovesTables />
      <SaengMyongEumNotes />
    </div>
  );
}

function SaengMyongEumMovesTables() {
  return (
    <>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Moves</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left font-medium">#</th>
                <th className="p-2 text-left font-medium">Movement</th>
                <th className="p-2 text-left font-medium">Stance</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "Left OH inside left to right parry (sweep the attack to the side)", "L"],
                ["2", "Left Sudo strike to the neck", "S"],
                ["3", "Right Punch to face", "S"],
                ["4", "Sweep inside with right to left Sudo parry face level", "S"],
                ["5", "Step R, left ridge hand strike to the neck (alone: R palm=target)", "R"],
                ["6", "Set in short back stance/fighting stance, OH LUB, OH RLB (Moo Gong)", "R"],
                ["7", "Left front kick to the stomach", ""],
                ["8", "Jump (or step) left front kick to the stomach", ""],
                ["9", "Step forward in R front stance R reverse downward Hammer strike", "R"],
                ["10", "Left reverse punch", "S"],
                ["11", "R OH Upper Block           Shift back in Back stance", "S"],
                ["12", "R OH Hand Drop Palm-heel Block", "S"],
                ["13", "Step back with right foot into open left-hand block from out to in/ right hand OH low block", "L"],
                ["14", "Step back left", "R"],
                ["15", "Step back right", "L"],
                ["16", "Step back left with L double sweep block to L side against M Punch", "R"],
                ["17", "Left Reverse Punch in Front stance (keep R hand guarding face)", "R"],
                ["18", "Set into back stance, OH RUB, OH LLB (Moo Gong)", "R"],
                ["19", "Step left, right roundhouse kick, set down in long right front stance", "R"],
                ["20", "R Hand Tiger mouth Neck grab Left low block simultaneously", "R"],
                ["21", "Step diagonally L forward to L and Cross hands in front of chest", "L Diagonal"],
                ["22", "Step forward, put head (butt) down, chest forward and open wings", "R"],
              ].map(([num, move, stance]) => (
                <tr key={num} className="border-b">
                  <td className="p-2 font-medium text-taekwondo-red">{num}</td>
                  <td className="p-2">{move}</td>
                  <td className="p-2">{stance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Ending 1</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left font-medium">#</th>
                <th className="p-2 text-left font-medium">Movement</th>
                <th className="p-2 text-left font-medium">Stance</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["23", "Double open hand blocks to left side (with left low, right shielding face) against Roundhouse kick", "R"],
                ["24", "Strike to neck with right Sudo and bring left hand up to block face", "S"],
                ["25", "Grab partners blocking arm Take a short step with left", "L"],
                ["26", "right outside sweep of partner's front leg, set right foot forward", "Short R"],
                ["27", "block a Sidekick with right open hand block", "Front stance"],
                ["28", "Left reverse Punch to the ground with right open hand up in the air, elbow bent 90°", "Low front stance"],
                ["29", "R Open hand low block (wipe left with right) left fist chamber", "Back"],
                ["30", "Bring left hand up like wing opening, unclench fist", "S"],
                ["31", "Bring left hand up like wing opening, open hand slightly", "S"],
                ["32", "Bring left hand up like wing opening, open completely", "S"],
                ["33", "Twist to right like X stance with left Sudo block counterclockwise", "S"],
              ].map(([num, move, stance]) => (
                <tr key={`e1-${num}`} className="border-b">
                  <td className="p-2 font-medium text-taekwondo-red">{num}</td>
                  <td className="p-2">{move}</td>
                  <td className="p-2">{stance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Ending 2</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left font-medium">#</th>
                <th className="p-2 text-left font-medium">Movement</th>
                <th className="p-2 text-left font-medium">Stance</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["23", "Double hand block to left side (with left low) against roundhouse kick", "R"],
                ["24", "Grab kick with left and strike to neck with right", "S"],
                ["25", "Grab collar/partners blocking arm, step up left", "L"],
                ["26", "right outside sweep of partner´s back leg and as the partner falls, release", "R"],
                ["27", "(Step short with left and) with right foot forward block a Sidekick from the ground with right open hand from out to in", "R"],
                ["28", "Left reverse Punch to the ground with right open hand up in the air, elbow bent 90°", "Low front stance"],
                ["29", "R Open hand low block (wipe left with right) left fist chamber", "S"],
                ["30", "Bring left hand up like wing opening", "S"],
                ["31", "Bring left hand up like wing opening", "S"],
                ["32", "Bring left hand up like wing opening", "S"],
                ["33", "Twist to right like X stance with left sudo block counterclockwise", "S"],
              ].map(([num, move, stance]) => (
                <tr key={`e2-${num}`} className="border-b">
                  <td className="p-2 font-medium text-taekwondo-red">{num}</td>
                  <td className="p-2">{move}</td>
                  <td className="p-2">{stance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function SaengMyongEumNotes() {
  return (
    <div>
      <h4 className="mb-3 text-base font-medium text-taekwondo-red">Notes</h4>
      <div className="space-y-4 text-gray-700">
        <div>
          <p className="font-medium">Middle transition:</p>
          <p>
            If switching roles, UM (A) steps up into Eagle stance and YANG (B) steps back into Left forward
            Eagle stance, then the roles switch.
          </p>
        </div>
        <div>
          <p className="font-medium">END:</p>
          <ol className="mt-2 list-none space-y-2">
            {[
              "Junbee Right leg back into Eagle Stance (Short back stance) Left foot Forward Fingertips forward",
              "Raise the arms like wings, keep them under the shoulders",
              "Raise the shoulders like hunching and raise the arms slightly higher, shaking the wings so the fingertips move, but the motion comes from the arms, not the fingers. The arms are completely relaxed.",
            ].map((line, index) => (
              <li key={index} className="flex gap-2">
                <span className="shrink-0 font-semibold text-taekwondo-red tabular-nums">{index + 1}.</span>
                <span>{line}</span>
              </li>
            ))}
          </ol>
          <p className="mt-2">UM (A) steps up, YANG (B) steps back to Junbee</p>
        </div>
      </div>
    </div>
  );
}

export default function SaengMyongEum({
  selectedBelt,
  getBeltColor: _getBeltColor,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: SaengMyongEumProps) {
  // Only show for D4 and above
  if (!["BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="SaengMyongEum">
            <TabsTrigger
              value="pattern-information"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Pattern Information
            </TabsTrigger>
            <TabsTrigger
              value="meaning"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Meaning
            </TabsTrigger>
            <TabsTrigger
              value="movements"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Movements
            </TabsTrigger>
            <TabsTrigger
              value="key-techniques"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Key Techniques
            </TabsTrigger>
            <TabsTrigger
              value="pattern"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="text-gray-700">
                SaengMyongEum is a grandmaster-level pattern that embodies the concept of life force energy
                and its manifestation through martial movement. This pattern is required for 4th Dan Black Belt
                and above.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="meaning" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
              <p className="text-gray-700">
                SaengMyongEum translates to &quot;Voice of Life Energy&quot; or &quot;Life Force Sound,&quot;
                representing the harmonious connection between internal energy (ki) and its external expression
                through movement and technique. The pattern emphasizes the practitioner&apos;s ability to
                manifest internal power through precise, dynamic movements.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="movements" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
              <p className="text-gray-700">
                The pattern consists of 64 movements that demonstrate the flow of life energy through advanced
                martial techniques. The movements follow a pattern that represents the cycle of life and
                energy, incorporating both circular and linear movements to express the dynamic nature of life
                force.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Energy projection techniques</li>
                <li>Advanced breathing methods</li>
                <li>Dynamic power generation</li>
                <li>Fluid transitional movements</li>
                <li>Internal energy cultivation</li>
                <li>Sound and movement coordination</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            <SaengMyongEumPatternTab />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg mt-2">
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Pattern Information</h4>
          <p className="text-gray-700 mb-4">
            SaengMyongEum is a grandmaster-level pattern that embodies the concept of life force energy and its manifestation through martial movement.
            This pattern is required for 4th Dan Black Belt and above.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Meaning</h4>
          <p className="text-gray-700">
            SaengMyongEum translates to "Voice of Life Energy" or "Life Force Sound," representing the harmonious connection between internal energy (ki) 
            and its external expression through movement and technique. The pattern emphasizes the practitioner's ability to manifest internal power 
            through precise, dynamic movements.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Movements</h4>
          <p className="text-gray-700">
            The pattern consists of 64 movements that demonstrate the flow of life energy through advanced martial techniques. The movements follow 
            a pattern that represents the cycle of life and energy, incorporating both circular and linear movements to express the dynamic nature 
            of life force.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Key Techniques</h4>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>Energy projection techniques</li>
            <li>Advanced breathing methods</li>
            <li>Dynamic power generation</li>
            <li>Fluid transitional movements</li>
            <li>Internal energy cultivation</li>
            <li>Sound and movement coordination</li>
          </ul>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Key</h4>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>OH = Open Hand</li>
            <li>R = Right</li>
            <li>L = Left</li>
            <li>S = Stay in Stance</li>
            <li>UB = Upper Block</li>
            <li>LB = Low Block</li>
            <li>M = Middle</li>
            <li>Sudo = Knife-hand strike</li>
          </ul>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Joonbi (Starting Position)</h4>
          <div className="text-gray-700 space-y-2">
            <p className="font-medium">START:</p>
            <p>Bow, Junbee (5 count)</p>
            <p>Both say "Saeng Myeong" = A says "Um." B says "Yang" Both say "Life!"</p>
            <ol className="list-decimal list-inside space-y-2 pl-4">
              <li>Right leg steps back into Eagle Stance (Short back stance) Fingertips forward</li>
              <li>Raise the arms like wings, keep them under the shoulders</li>
              <li>Raise the breastbone and raise the arms slightly higher, shaking the wings from the shoulders so the fingertips move; the motion comes from the arms, not the fingers. The arms are completely relaxed.</li>
            </ol>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Moves</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-2 font-medium">#</th>
                  <th className="text-left p-2 font-medium">Movement</th>
                  <th className="text-left p-2 font-medium">Stance</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["1", "Left OH inside left to right parry (sweep the attack to the side)", "L"],
                  ["2", "Left Sudo strike to the neck", "S"],
                  ["3", "Right Punch to face", "S"],
                  ["4", "Sweep inside with right to left Sudo parry face level", "S"],
                  ["5", "Step R, left ridge hand strike to the neck (alone: R palm=target)", "R"],
                  ["6", "Set in short back stance/fighting stance, OH LUB, OH RLB (Moo Gong)", "R"],
                  ["7", "Left front kick to the stomach", ""],
                  ["8", "Jump (or step) left front kick to the stomach", ""],
                  ["9", "Step forward in R front stance R reverse downward Hammer strike", "R"],
                  ["10", "Left reverse punch", "S"],
                  ["11", "R OH Upper Block           Shift back in Back stance", "S"],
                  ["12", "R OH Hand Drop Palm-heel Block", "S"],
                  ["13", "Step back with right foot into open left-hand block from out to in/ right hand OH low block", "L"],
                  ["14", "Step back left", "R"],
                  ["15", "Step back right", "L"],
                  ["16", "Step back left with L double sweep block to L side against M Punch", "R"],
                  ["17", "Left Reverse Punch in Front stance (keep R hand guarding face)", "R"],
                  ["18", "Set into back stance, OH RUB, OH LLB (Moo Gong)", "R"],
                  ["19", "Step left, right roundhouse kick, set down in long right front stance", "R"],
                  ["20", "R Hand Tiger mouth Neck grab Left low block simultaneously", "R"],
                  ["21", "Step diagonally L forward to L and Cross hands in front of chest", "L Diagonal"],
                  ["22", "Step forward, put head (butt) down, chest forward and open wings", "R"]
                ].map(([num, move, stance]) => (
                  <tr key={num} className="border-b">
                    <td className="p-2 text-taekwondo-red font-medium">{num}</td>
                    <td className="p-2">{move}</td>
                    <td className="p-2">{stance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Ending 1</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-2 font-medium">#</th>
                  <th className="text-left p-2 font-medium">Movement</th>
                  <th className="text-left p-2 font-medium">Stance</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["23", "Double open hand blocks to left side (with left low, right shielding face) against Roundhouse kick", "R"],
                  ["24", "Strike to neck with right Sudo and bring left hand up to block face", "S"],
                  ["25", "Grab partners blocking arm Take a short step with left", "L"],
                  ["26", "right outside sweep of partner's front leg, set right foot forward", "Short R"],
                  ["27", "block a Sidekick with right open hand block", "Front stance"],
                  ["28", "Left reverse Punch to the ground with right open hand up in the air, elbow bent 90°", "Low front stance"],
                  ["29", "R Open hand low block (wipe left with right) left fist chamber", "Back"],
                  ["30", "Bring left hand up like wing opening, unclench fist", "S"],
                  ["31", "Bring left hand up like wing opening, open hand slightly", "S"],
                  ["32", "Bring left hand up like wing opening, open completely", "S"],
                  ["33", "Twist to right like X stance with left Sudo block counterclockwise", "S"]
                ].map(([num, move, stance]) => (
                  <tr key={num} className="border-b">
                    <td className="p-2 text-taekwondo-red font-medium">{num}</td>
                    <td className="p-2">{move}</td>
                    <td className="p-2">{stance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Ending 2</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-2 font-medium">#</th>
                  <th className="text-left p-2 font-medium">Movement</th>
                  <th className="text-left p-2 font-medium">Stance</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["23", "Double hand block to left side (with left low) against roundhouse kick", "R"],
                  ["24", "Grab kick with left and strike to neck with right", "S"],
                  ["25", "Grab collar/partners blocking arm, step up left", "L"],
                  ["26", "right outside sweep of partner´s back leg and as the partner falls, release", "R"],
                  ["27", "(Step short with left and) with right foot forward block a Sidekick from the ground with right open hand from out to in", "R"],
                  ["28", "Left reverse Punch to the ground with right open hand up in the air, elbow bent 90°", "Low front stance"],
                  ["29", "R Open hand low block (wipe left with right) left fist chamber", "S"],
                  ["30", "Bring left hand up like wing opening", "S"],
                  ["31", "Bring left hand up like wing opening", "S"],
                  ["32", "Bring left hand up like wing opening", "S"],
                  ["33", "Twist to right like X stance with left sudo block counterclockwise", "S"]
                ].map(([num, move, stance]) => (
                  <tr key={num} className="border-b">
                    <td className="p-2 text-taekwondo-red font-medium">{num}</td>
                    <td className="p-2">{move}</td>
                    <td className="p-2">{stance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Notes</h4>
          <div className="text-gray-700 space-y-4">
            <div>
              <p className="font-medium">Middle transition:</p>
              <p>If switching roles, UM (A) steps up into Eagle stance and YANG (B) steps back into Left forward Eagle stance, then the roles switch.</p>
            </div>
            <div>
              <p className="font-medium">END:</p>
              <ol className="list-decimal list-inside space-y-2 pl-4">
                <li>Junbee Right leg back into Eagle Stance (Short back stance) Left foot Forward Fingertips forward</li>
                <li>Raise the arms like wings, keep them under the shoulders</li>
                <li>Raise the shoulders like hunching and raise the arms slightly higher, shaking the wings so the fingertips move, but the motion comes from the arms, not the fingers. The arms are completely relaxed.</li>
              </ol>
              <p className="mt-2">UM (A) steps up, YANG (B) steps back to Junbee</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 